import { useState, useRef, useEffect, Fragment } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import api from '../services/api'
import AvisoUltimaNota from '../components/AvisoUltimaNota'
import IbsCbsSelect from '../components/IbsCbsSelect'
import NaturezaSelect from '../components/NaturezaSelect'
import LookupSelect from '../components/LookupSelect'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const UF_IBGE: Record<string, string> = {
  AC:'12',AL:'27',AP:'16',AM:'13',BA:'29',CE:'23',DF:'53',ES:'32',
  GO:'52',MA:'21',MT:'51',MS:'50',MG:'31',PA:'15',PB:'25',PR:'41',
  PE:'26',PI:'22',RJ:'33',RN:'24',RS:'43',RO:'11',RR:'14',SC:'42',
  SP:'35',SE:'28',TO:'17',
}
function calcDV(c43: string): number {
  const p = [2,3,4,5,6,7,8,9]; let s = 0
  for (let i = c43.length-1, j=0; i>=0; i--,j++) s += parseInt(c43[i]) * p[j%8]
  const r = s % 11; return r < 2 ? 0 : 11 - r
}
function gerarChave(empcgc: string, empest: string, dataEmissao: string, serie: string, numero: string, cNF: string) {
  const cuf = UF_IBGE[empest.trim()] ?? '43'
  const aamm = dataEmissao.replace(/-/g,'').slice(2,6)
  const cnpj = empcgc.replace(/\D/g,'').padStart(14,'0')
  const ser = serie.padStart(3,'0'); const num = numero.padStart(9,'0')
  const c43 = `${cuf}${aamm}${cnpj}55${ser}${num}1${cNF}`
  return c43 + calcDV(c43)
}
function randCNF() { return String(Math.floor(Math.random()*99999999)).padStart(8,'0') }
function formatDoc(doc: string) {
  const n = doc.replace(/\D/g,'')
  if (n.length===14) return n.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,'$1.$2.$3/$4-$5')
  if (n.length===11) return n.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/,'$1.$2.$3-$4')
  return doc
}
const r2 = (n: number) => Math.round(n*100)/100
const brl = (n: number) => Number(n||0).toLocaleString('pt-BR',{style:'currency',currency:'BRL'})

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface Empresa { empcod: number; empnom: string; empcgc: string; empest: string }
interface Natureza {
  tno_cod: number; tno_cdf: number; tno_des: string; tno_dsc: string; tno_nat: string
  tno_tic: string; tno_bic: number; tno_aic: number   // ICMS
  tno_tpi: string; tno_bpi: number; tno_api: number   // PIS
  tno_tcf: string; tno_bcf: number; tno_acf: number   // COFINS
  tno_tin: string; tno_bin: number; tno_ain: number   // IPI
}
interface IbsCbs {
  cst: string; classTrib: string; classTribDes: string
  cbsBc: number; cbsBcEf: number; cbsAliRef: number; cbsRedAli: number; cbsAliEf: number; cbsValor: number
  ibsUfEst: string; ibsUfBcEf: number; ibsUfAliRef: number; ibsUfRedAli: number; ibsUfAliEf: number; ibsUfValor: number
  ibsMunBcEf: number; ibsMunAliRef: number; ibsMunRedAli: number; ibsMunAliEf: number; ibsMunValor: number
  isSbas: number; isSali: number
}
const ibsCbsVazio: IbsCbs = {
  cst:'', classTrib:'', classTribDes:'',
  cbsBc:0, cbsBcEf:0, cbsAliRef:0, cbsRedAli:0, cbsAliEf:0, cbsValor:0,
  ibsUfEst:'', ibsUfBcEf:0, ibsUfAliRef:0, ibsUfRedAli:0, ibsUfAliEf:0, ibsUfValor:0,
  ibsMunBcEf:0, ibsMunAliRef:0, ibsMunRedAli:0, ibsMunAliEf:0, ibsMunValor:0,
  isSbas:0, isSali:0,
}
// Reescala os valores monetários de IBS/CBS pela proporção da quantidade devolvida
// (alíquotas e campos de texto não mudam, só base/valor). Sem isso, devolver menos
// do que a quantidade original mantinha o vCBS/vIBS da nota cheia, enquanto o vProd
// do item já vinha menor — gerando o cStat 1069 "Valor da CBS difere do calculado".
function escalarIbsCbs(orig: IbsCbs, ratio: number): IbsCbs {
  if (!orig.cst) return {...orig}
  return {
    ...orig,
    cbsBc: r2(orig.cbsBc*ratio), cbsBcEf: r2(orig.cbsBcEf*ratio), cbsValor: r2(orig.cbsValor*ratio),
    ibsUfBcEf: r2(orig.ibsUfBcEf*ratio), ibsUfValor: r2(orig.ibsUfValor*ratio),
    ibsMunBcEf: r2(orig.ibsMunBcEf*ratio), ibsMunValor: r2(orig.ibsMunValor*ratio),
    isSbas: r2(orig.isSbas*ratio),
  }
}
function calcIbsCbs(total: number, r: any): IbsCbs {
  if (!r || total<=0) return {...ibsCbsVazio}
  const bcEfC = r2(total*(1-Number(r.ibs08_crbc)/100))
  const bcEfU = r2(total*(1-Number(r.ibs08_urbc)/100))
  const bcEfM = r2(total*(1-Number(r.ibs08_mrbc)/100))
  return {
    cst: r.ibs08_cst?.trim()??'', classTrib: r.ibs08_tri?.trim()??'', classTribDes: r.ibs08_d02?.trim()??'',
    cbsBc: total, cbsBcEf: bcEfC, cbsAliRef: Number(r.ibs08_cali), cbsRedAli: Number(r.ibs08_cpra), cbsAliEf: Number(r.ibs08_caef), cbsValor: r2(bcEfC*Number(r.ibs08_caef)/100),
    ibsUfEst: r.ibs08_uest?.trim()??'', ibsUfBcEf: bcEfU, ibsUfAliRef: Number(r.ibs08_uali), ibsUfRedAli: Number(r.ibs08_upra), ibsUfAliEf: Number(r.ibs08_uaef), ibsUfValor: r2(bcEfU*Number(r.ibs08_uaef)/100),
    ibsMunBcEf: bcEfM, ibsMunAliRef: Number(r.ibs08_mali), ibsMunRedAli: Number(r.ibs08_mpra), ibsMunAliEf: Number(r.ibs08_maef), ibsMunValor: r2(bcEfM*Number(r.ibs08_maef)/100),
    isSbas: Number(r.ibs08_sbas), isSali: Number(r.ibs08_sali),
  }
}

interface Cabecalho {
  empcod: number; serie: string; numero: number
  cnpj_fornecedor: string; chave: string; data_emissao: string
  valor_total: number; cfop_original: string; nome_fornecedor: string
  fj: string; ie: string; uf: string; contribuinte: string
  logradouro: string; numero_end: string; bairro: string
  cep: string; telefone: string; cidade: string
  bloqueado?: boolean
}
interface ItemDevolucao {
  lin: number; its_cod: number; descricao: string; unidade: string
  quantidade: number; preco_unitario: number; ncm: string; cest: string
  // original tax values (from DB, unchanged)
  icms_aliq: number; icms_base_orig: number; icms_valor_orig: number; icms_cst: string
  pis_aliq: number;  pis_base_orig: number;  pis_valor_orig: number
  cofins_aliq: number; cofins_base_orig: number; cofins_valor_orig: number
  // UI state
  selecionado: boolean; qtd_devol: string; expandido: boolean
  // editable (auto-proportional, user-overridable)
  edit_icms_cst: string; edit_icms_base: number; edit_icms_aliq: number; edit_icms_valor: number
  edit_st_base: number; edit_st_aliq: number; edit_st_valor: number
  edit_ipi_cst: string; edit_ipi_base: number; edit_ipi_aliq: number; edit_ipi_valor: number
  edit_pis_cst: string;  edit_pis_base: number;  edit_pis_aliq: number;  edit_pis_valor: number
  edit_cofins_cst: string; edit_cofins_base: number; edit_cofins_aliq: number; edit_cofins_valor: number
  ibsCbs: IbsCbs
  // IBS/CBS da nota de entrada original, na quantidade cheia — usado como base pra
  // reescalar `ibsCbs` proporcionalmente quando o usuário edita qtd_devol (ver atualizarQtd)
  ibsCbsOrig: IbsCbs
  // Grupo Combustível — só obrigatório quando o CFOP é "de combustível" (ver CFOPS_COMBUSTIVEL)
  comb_anp?: string; comb_desc?: string
  // Frete da nota de entrada (qtd cheia) e o proporcional editável que vai no XML
  frete_orig?: number; edit_frete?: number
}

// CFOPs de operação com combustível/lubrificante (família 565x/566x/665x/666x) —
// SEFAZ exige o Grupo Combustível no item quando o CFOP é um desses (rejeição 660).
const CFOPS_COMBUSTIVEL = new Set([
  '5651','5652','5653','5654','5655','5656','5661','5662','5663',
  '6651','6652','6653','6654','6655','6656','6661','6662','6663',
])

// Painel de Impostos/IBS-CBS de um item — usado tanto na etapa de configuração
// quanto na revisão final, pra não duplicar essa tabela grande duas vezes.
function PainelImpostosItem({ it, aba, onAba, onEdit, somenteLeitura, cfop }: {
  it: ItemDevolucao
  aba: 'impostos' | 'ibscbs'
  onAba: (aba: 'impostos' | 'ibscbs') => void
  onEdit: (field: string, value: any) => void
  somenteLeitura?: boolean
  cfop?: string
}) {
  const exigeComb = !!cfop && CFOPS_COMBUSTIVEL.has(cfop)
  return (
    <>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8, flexWrap:'wrap' }}>
        <label style={{ fontSize:'0.72rem', fontWeight:700, color:'#6b7280', textTransform:'uppercase' }}>NCM</label>
        <input type="text" maxLength={8} readOnly={somenteLeitura}
          value={it.ncm}
          onChange={e => onEdit('ncm', e.target.value.replace(/\D/g,''))}
          placeholder="8 dígitos"
          style={{
            width:100, padding:'3px 6px', borderRadius:4, fontSize:'0.8rem',
            border: it.ncm.length === 8 ? '1px solid #d1d5db' : '1.5px solid #dc2626',
            background: it.ncm.length === 8 ? '#fff' : '#fef2f2',
          }} />
        {it.ncm.length !== 8 && <span style={{ fontSize:'0.75rem', color:'#dc2626' }}>obrigatório, 8 dígitos</span>}

        {exigeComb && <>
          <label style={{ fontSize:'0.72rem', fontWeight:700, color:'#6b7280', textTransform:'uppercase', marginLeft:16 }}>Cód. ANP</label>
          <input type="text" maxLength={9} readOnly={somenteLeitura}
            value={it.comb_anp ?? ''}
            onChange={e => onEdit('comb_anp', e.target.value.replace(/\D/g,''))}
            placeholder="9 dígitos"
            style={{
              width:100, padding:'3px 6px', borderRadius:4, fontSize:'0.8rem',
              border: (it.comb_anp?.length ?? 0) === 9 ? '1px solid #d1d5db' : '1.5px solid #dc2626',
              background: (it.comb_anp?.length ?? 0) === 9 ? '#fff' : '#fef2f2',
            }} />
          <label style={{ fontSize:'0.72rem', fontWeight:700, color:'#6b7280', textTransform:'uppercase' }}>Descr. ANP</label>
          <input type="text" readOnly={somenteLeitura}
            value={it.comb_desc ?? ''}
            onChange={e => onEdit('comb_desc', e.target.value)}
            placeholder="descrição do produto na ANP"
            style={{ width:220, padding:'3px 6px', borderRadius:4, fontSize:'0.8rem', border:'1px solid #d1d5db' }} />
          {(it.comb_anp?.length ?? 0) !== 9 && (
            <span style={{ fontSize:'0.75rem', color:'#dc2626', width:'100%' }}>
              CFOP {cfop} exige o código ANP (grupo Combustível) — confira em simp.anp.gov.br antes de preencher.
            </span>
          )}
        </>}
      </div>
      <div className="fiscal-tabs">
        <button type="button"
          className={`fiscal-tab${aba==='impostos'?' fiscal-tab--ativa':''}`}
          onClick={() => onAba('impostos')}>Impostos</button>
        <button type="button"
          className={`fiscal-tab${aba==='ibscbs'?' fiscal-tab--ativa':''}`}
          onClick={() => onAba('ibscbs')}>IBS/CBS</button>
      </div>

      {aba === 'impostos' && (
        <table className="imp-table">
          <thead>
            <tr>
              <th style={{ width:70 }}>Tributo</th>
              <th style={{ width:125 }}>Base Cálculo</th>
              <th style={{ width:75 }}>Alíquota</th>
              <th style={{ width:125 }}>Destaque</th>
              <th style={{ width:60 }}>CST</th>
            </tr>
          </thead>
          <tbody>
            {[
              { label:'ICMS',    bc:'edit_icms_base',   aliq:'edit_icms_aliq',   dest:'edit_icms_valor',   cst:'edit_icms_cst' },
              { label:'ICMS ST', bc:'edit_st_base',     aliq:'edit_st_aliq',     dest:'edit_st_valor',     cst:null },
              { label:'IPI',     bc:'edit_ipi_base',    aliq:'edit_ipi_aliq',    dest:'edit_ipi_valor',    cst:'edit_ipi_cst' },
              { label:'PIS',     bc:'edit_pis_base',    aliq:'edit_pis_aliq',    dest:'edit_pis_valor',    cst:'edit_pis_cst' },
              { label:'COFINS',  bc:'edit_cofins_base', aliq:'edit_cofins_aliq', dest:'edit_cofins_valor', cst:'edit_cofins_cst' },
            ].map(row => (
              <tr key={row.label}>
                <td className="imp-label">{row.label}</td>
                <td><input type="number" className="imp-input" min={0} step={0.01} readOnly={somenteLeitura}
                  value={(it as any)[row.bc]}
                  onChange={e => onEdit(row.bc, Number(e.target.value))} /></td>
                <td><input type="number" className="imp-input imp-aliq" min={0} step={0.001} readOnly={somenteLeitura}
                  value={(it as any)[row.aliq]}
                  onChange={e => onEdit(row.aliq, Number(e.target.value))} /></td>
                <td><input type="number" className="imp-input" min={0} step={0.01} readOnly={somenteLeitura}
                  value={(it as any)[row.dest]}
                  onChange={e => onEdit(row.dest, Number(e.target.value))} /></td>
                <td>{row.cst
                  ? <input type="text" className="imp-input imp-cst" readOnly={somenteLeitura}
                      value={(it as any)[row.cst]}
                      onChange={e => onEdit(row.cst!, e.target.value)} />
                  : <span className="imp-vazio">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {aba === 'ibscbs' && (
        <div>
          <table className="imp-table" style={{ marginTop:'0.35rem' }}>
            <thead>
              <tr>
                <th style={{ width:65 }}>Tributo</th>
                <th style={{ width:55 }}>CST</th>
                <th style={{ width:110 }}>Base Cálculo</th>
                <th style={{ width:115 }}>BC Efetiva</th>
                <th style={{ width:75 }}>Ali. Ref %</th>
                <th style={{ width:75 }}>Red. Ali %</th>
                <th style={{ width:75 }}>Ali. Efet %</th>
                <th style={{ width:110 }}>Valor</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="imp-label">CBS</td>
                <td style={{ textAlign:'center', fontSize:'0.78rem' }}>{it.ibsCbs.cst}</td>
                <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.cbsBc.toFixed(2)} /></td>
                <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.cbsBcEf.toFixed(2)} /></td>
                <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.cbsAliRef.toFixed(2)} /></td>
                <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.cbsRedAli.toFixed(2)} /></td>
                <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.cbsAliEf.toFixed(2)} /></td>
                <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.cbsValor.toFixed(2)} style={{ fontWeight:800, color:'#1a6ea8' }} /></td>
              </tr>
              <tr>
                <td className="imp-label">IBS UF</td>
                <td style={{ textAlign:'center', fontSize:'0.78rem', color:'#6b7280' }}>{it.ibsCbs.ibsUfEst}</td>
                <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.cbsBc.toFixed(2)} /></td>
                <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.ibsUfBcEf.toFixed(2)} /></td>
                <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.ibsUfAliRef.toFixed(2)} /></td>
                <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.ibsUfRedAli.toFixed(2)} /></td>
                <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.ibsUfAliEf.toFixed(2)} /></td>
                <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.ibsUfValor.toFixed(2)} style={{ fontWeight:800, color:'#1a6ea8' }} /></td>
              </tr>
              <tr>
                <td className="imp-label">IBS MUN</td>
                <td></td>
                <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.cbsBc.toFixed(2)} /></td>
                <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.ibsMunBcEf.toFixed(2)} /></td>
                <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.ibsMunAliRef.toFixed(2)} /></td>
                <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.ibsMunRedAli.toFixed(2)} /></td>
                <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.ibsMunAliEf.toFixed(2)} /></td>
                <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.ibsMunValor.toFixed(2)} style={{ fontWeight:800, color:'#1a6ea8' }} /></td>
              </tr>
              <tr>
                <td className="imp-label">IS</td>
                <td></td>
                <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.isSbas.toFixed(2)} /></td>
                <td colSpan={3}></td>
                <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.isSali.toFixed(2)} /></td>
                <td></td>
              </tr>
            </tbody>
          </table>
          {it.ibsCbs.classTrib && (
            <div style={{ marginTop:'0.35rem', fontSize:'0.75rem', color:'#6b7280' }}>
              <strong>Class. Trib.:</strong> {it.ibsCbs.classTrib} — {it.ibsCbs.classTribDes}
            </div>
          )}
          {!it.ibsCbs.cst && (
            <div style={{ padding:'12px 0', fontSize:'0.82rem', color:'#9ca3af', textAlign:'center' }}>
              Nenhuma regra IBS/CBS selecionada
            </div>
          )}
        </div>
      )}
    </>
  )
}

// ─── Componente ───────────────────────────────────────────────────────────────

export default function EmitirDevolucaoNFe() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const editId = searchParams.get('edit')
  const idSalvoRef = useRef<number | null>(null) // nota já gravada nesta tela (reemissão depois de rejeição)

  const [etapa, setEtapa] = useState(0)

  // Empresa
  const [empresas, setEmpresas] = useState<Empresa[]>([])
  const [empresa, setEmpresa]   = useState<Empresa | null>(null)

  // Busca
  const [chaveInput, setChaveInput] = useState('')
  const [empcod, setEmpcod] = useState('')
  const [cnpj, setCnpj]     = useState('')
  const [serie, setSerie]   = useState('')
  const [numBusca, setNumBusca] = useState('')
  const [buscando, setBuscando] = useState(false)
  const [erroBusca, setErroBusca] = useState('')

  // Nota original + próximo número
  const [cab, setCab]           = useState<Cabecalho | null>(null)
  const [itens, setItens]       = useState<ItemDevolucao[]>([])
  const [modFrete, setModFrete] = useState<'0'|'1'|'2'|'3'|'4'|'9'>('9')
  const [proxSerie, setProxSerie]   = useState('')
  const [proxNumero, setProxNumero] = useState('')
  const [chaveNFe, setChaveNFe]     = useState('')

  // Configuração
  const [naturezas, setNaturezas]   = useState<Natureza[]>([])
  const [naturezaCod, setNaturezaCod] = useState('')
  const [dataEmissao, setDataEmissao] = useState(new Date().toISOString().slice(0,10))
  const [obs, setObs]               = useState('')
  // Doc. Financeiro: vem da TPARVEN (Doc. Fin. p/ Devolução); vazio lá, o usuário escolhe
  const [tipdcoParam, setTipdcoParam] = useState<string | null>(null)
  const [tipdco, setTipdco]           = useState('')
  const [tipdcoDes, setTipdcoDes]     = useState('')
  const [ibsCbsCod, setIbsCbsCod]   = useState('')
  const [ibsCbsDes, setIbsCbsDes]   = useState('')
  // Deixa o usuário escolher a regra manualmente mesmo quando algum item já veio com
  // um IBS/CBS "herdado" (da nota de entrada ou de uma tentativa salva anterior) —
  // útil quando esse valor herdado está errado/desatualizado, ex.: a nota de entrada
  // não gravou IBS/CBS de verdade e o que aparece é resíduo de um rascunho anterior.
  const [ibsCbsForcarManual, setIbsCbsForcarManual] = useState(false)

  // Financeiro
  const [financeiroModal, setFinanceiroModal] = useState(false)
  const [finQtdParcelas, setFinQtdParcelas]   = useState(1)
  const [finIntervalo, setFinIntervalo]       = useState(30)
  const [finParcelas, setFinParcelas]         = useState<{numero:string;vencimento:string;valor:string}[]>([])
  const [finContaCod, setFinContaCod]         = useState('')
  const [finContaNome, setFinContaNome]       = useState('')
  const [finContaRes, setFinContaRes]         = useState<{dep_cod:string;dep_des:string}[]>([])
  const [finContaAberto, setFinContaAberto]   = useState(false)

  // Abas fiscais por item
  const [abaFiscal, setAbaFiscal] = useState<Record<number,'impostos'|'ibscbs'>>({})
  // Expandir impostos na tela de revisão (etapa 1) — chave por número de linha
  // do item (lin), não por índice, porque itensSel é filtrado de itens.
  const [expandRevisao, setExpandRevisao] = useState<Record<number, boolean>>({})
  const abaItem = (i: number) => abaFiscal[i] ?? 'impostos'

  // Emissão
  const [enviando, setEnviando]   = useState(false)
  const [resultado, setResultado] = useState<string|null>(null)
  const [erro, setErro]           = useState<string|null>(null)

  useEffect(() => {
    api.get('/erp/empresas').then(r => {
      setEmpresas(r.data)
      if (r.data.length > 0) setEmpresa(r.data[0])
    }).catch(() => {})
    api.get('/erp/naturezas').then(r => {
      setNaturezas(r.data.filter((n: Natureza) => n.tno_nat === '3'))
    }).catch(() => {})
  }, [])
  useEffect(() => { if (empresa) setEmpcod(String(empresa.empcod)) }, [empresa])

  // Doc. Financeiro da TPARVEN: preenchido lá, vale ele (campo travado); vazio, o usuário escolhe
  const empcodDoc = empresa?.empcod ?? cab?.empcod
  useEffect(() => {
    if (!empcodDoc) return
    api.get(`/nfe/devolucao/tipdco/${empcodDoc}`).then(r => {
      const doc = String(r.data?.tipdco ?? '')
      setTipdcoParam(doc)
      if (doc) { setTipdco(doc); setTipdcoDes(r.data?.descricao ?? '') }
    }).catch(() => setTipdcoParam(''))
  }, [empcodDoc])

  // ── Modo edição: carrega payload da nota rejeitada ─────────────────────────
  useEffect(() => {
    if (!editId || !empresas.length || !naturezas.length) return
    api.get(`/nfe/${editId}/payload`).then(r => {
      const fp  = r.data?._formPayload
      const raw = r.data?._dadosNFe ?? r.data

      if (fp) {
        // Payload completo salvo a partir da nova versão
        const emp = empresas.find(e => e.empcod === fp.empcod) ?? empresas[0]
        setEmpresa(emp)
        setCab(fp.cab)
        // rascunhos salvos antes do campo ibsCbsOrig existir não têm essa chave —
        // completa com o próprio ibsCbs pra não quebrar a reescala em atualizarQtd
        setItens((fp.itens ?? []).map((it: ItemDevolucao) => ({ ...it, ibsCbsOrig: it.ibsCbsOrig ?? it.ibsCbs })))
        setNaturezaCod(String(fp.naturezaCod ?? ''))
        setDataEmissao(fp.dataEmissao ?? new Date().toISOString().slice(0,10))
        setObs(fp.obs ?? '')
        if (fp.tipdco) { setTipdco(fp.tipdco); setTipdcoDes(fp.tipdcoDes ?? '') }
        setProxSerie(fp.proxSerie ?? '')
        setProxNumero(fp.proxNumero ?? '')
        setChaveNFe(fp.chaveNFe ?? '')
        setModFrete(fp.modFrete ?? '9')
        if (fp.finParcelas?.length) setFinParcelas(fp.finParcelas)
        if (fp.finContaCod) setFinContaCod(fp.finContaCod)
        if (fp.ibsCbsCod) setIbsCbsCod(fp.ibsCbsCod)
        if (fp.ibsCbsDes) setIbsCbsDes(fp.ibsCbsDes)
      } else if (raw?.finNFe === '4') {
        // Fallback: reconstrói a partir do _dadosNFe (notas anteriores à versão com _formPayload)
        const emp = empresas.find(e => e.empcod === raw.empcod) ?? empresas[0]
        setEmpresa(emp)
        const d = raw.destinatario ?? {}
        setCab({
          empcod:          raw.empcod,
          serie:           raw.serie,
          numero:          raw.numero,
          cnpj_fornecedor: d.doc ?? '',
          chave:           raw.nfRef?.[0] ?? '',
          data_emissao:    raw.dataEmissao ?? '',
          valor_total:     raw.itens?.reduce((s: number, it: any) => s + (it.vProd || 0), 0) ?? 0,
          cfop_original:   String(raw.natureza?.cfop ?? ''),
          nome_fornecedor: d.nome ?? '',
          fj:              d.fj ?? 'J',
          ie:              d.ie ?? '',
          uf:              d.endereco?.uf ?? '',
          contribuinte:    d.indIEDest === '1' ? 'S' : 'N',
          logradouro:      d.endereco?.logradouro ?? '',
          numero_end:      d.endereco?.numero ?? '',
          bairro:          d.endereco?.bairro ?? '',
          cep:             d.endereco?.cep ?? '',
          telefone:        d.endereco?.telefone ?? '',
          cidade:          d.endereco?.cidade ?? '',
        })
        setItens((raw.itens ?? []).map((it: any, idx: number) => ({
          lin:              idx + 1,
          its_cod:          parseInt(it.cProd) || 0,
          descricao:        it.xProd ?? '',
          unidade:          it.uCom ?? 'UN',
          quantidade:       it.qCom ?? 0,
          preco_unitario:   it.vUnCom ?? 0,
          ncm:              it.NCM ?? '',
          cest:             it.CEST ?? '',
          icms_aliq:        it.impostos?.icmsAliq ?? 0,
          icms_base_orig:   it.impostos?.icmsBase ?? 0,
          icms_valor_orig:  it.impostos?.icmsDest ?? 0,
          icms_cst:         it.impostos?.icmsCst ?? '41',
          pis_aliq:         it.impostos?.pisAliq ?? 0,
          pis_base_orig:    it.impostos?.pisBase ?? 0,
          pis_valor_orig:   it.impostos?.pisDest ?? 0,
          cofins_aliq:      it.impostos?.cofinsAliq ?? 0,
          cofins_base_orig: it.impostos?.cofinsBase ?? 0,
          cofins_valor_orig:it.impostos?.cofinsDest ?? 0,
          selecionado:  true,
          qtd_devol:    String(it.qCom ?? 0),
          expandido:    true,
          edit_icms_cst:   it.impostos?.icmsCst ?? '41',
          edit_icms_base:  it.impostos?.icmsBase ?? 0,
          edit_icms_aliq:  it.impostos?.icmsAliq ?? 0,
          edit_icms_valor: it.impostos?.icmsDest ?? 0,
          edit_st_base:  it.impostos?.stBase ?? 0,
          edit_st_aliq:  it.impostos?.stAliq ?? 0,
          edit_st_valor: it.impostos?.stDest ?? 0,
          edit_ipi_cst:    it.impostos?.ipiCst ?? '53',
          edit_ipi_base:   it.impostos?.ipiBase ?? 0,
          edit_ipi_aliq:   it.impostos?.ipiAliq ?? 0,
          edit_ipi_valor:  it.impostos?.ipiDest ?? 0,
          edit_pis_cst:    it.impostos?.pisCst ?? '07',
          edit_pis_base:   it.impostos?.pisBase ?? 0,
          edit_pis_aliq:   it.impostos?.pisAliq ?? 0,
          edit_pis_valor:  it.impostos?.pisDest ?? 0,
          edit_cofins_cst:    it.impostos?.cofinsCst ?? '07',
          edit_cofins_base:   it.impostos?.cofinsBase ?? 0,
          edit_cofins_aliq:   it.impostos?.cofinsAliq ?? 0,
          edit_cofins_valor:  it.impostos?.cofinsDest ?? 0,
          // Este fallback reconstrói a partir do XML salvo (_dadosNFe), que não guarda
          // os campos auxiliares de IBS/CBS (BC ref., % redução) — só o que foi pro XML.
          // Sem a regra original, o usuário tem que reinformar em "IBS/CBS — Regra".
          ibsCbs: { ...ibsCbsVazio },
          ibsCbsOrig: { ...ibsCbsVazio },
          frete_orig: it.vFrete ?? 0, edit_frete: it.vFrete ?? 0,
        })))
        const natMatch = naturezas.find(n => n.tno_cdf === raw.natureza?.cfop)
        if (natMatch) setNaturezaCod(String(natMatch.tno_cod))
        setDataEmissao(raw.dataEmissao ?? new Date().toISOString().slice(0,10))
        setObs(raw.obs ?? '')
        setProxSerie(String(raw.serie ?? ''))
        setProxNumero(String(raw.numero ?? ''))
        setChaveNFe(raw.chave ?? '')
        setModFrete(raw.modFrete ?? '9')
        const dups = raw.duplicatas ?? []
        if (dups.length) setFinParcelas(dups.map((d: any) => ({ numero: d.nDup, vencimento: d.dVenc, valor: String(d.vDup) })))
      }

      setEtapa(1)
    }).catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, empresas.length, naturezas.length])

  const chaveDigits      = chaveInput.replace(/\D/g,'')
  const podeBuscarChave  = chaveDigits.length === 44
  const podeBuscarCampos = !!(empcod && cnpj && serie && numBusca)
  const podeBuscar       = podeBuscarChave || podeBuscarCampos

  async function buscar() {
    setErroBusca(''); setCab(null); setItens([]); setProxNumero(''); setChaveNFe('')
    setBuscando(true)
    try {
      const url = podeBuscarChave
        ? `/nfe/devolucao/buscar?chave=${chaveDigits}`
        : `/nfe/devolucao/buscar?empcod=${empcod}&cnpj=${cnpj.replace(/\D/g,'')}&serie=${serie}&numero=${numBusca}`
      const r = await api.get(url)
      const { cabecalho, itens: its } = r.data
      setCab(cabecalho)
      setModFrete(String(cabecalho.mod_frete ?? 9) as any)
      setItens(its.map((it: any) => {
        const ibsCbs: IbsCbs = it.ibs_cbs_cst ? {
          cst: it.ibs_cbs_cst, classTrib: it.ibs_cbs_classtrib, classTribDes: it.ibs_cbs_classtrib_des ?? '',
          cbsBc: Number(it.cbs_base), cbsBcEf: Number(it.cbs_bc_ef), cbsAliRef: Number(it.cbs_ali_ref), cbsRedAli: Number(it.cbs_red_ali), cbsAliEf: Number(it.cbs_ali_ef), cbsValor: Number(it.cbs_valor),
          ibsUfEst: it.ibs_uf_est ?? '', ibsUfBcEf: Number(it.ibs_uf_bc_ef), ibsUfAliRef: Number(it.ibs_uf_ali_ref), ibsUfRedAli: Number(it.ibs_uf_red_ali), ibsUfAliEf: Number(it.ibs_uf_ali_ef), ibsUfValor: Number(it.ibs_uf_valor),
          ibsMunBcEf: Number(it.ibs_mun_bc_ef), ibsMunAliRef: Number(it.ibs_mun_ali_ref), ibsMunRedAli: Number(it.ibs_mun_red_ali), ibsMunAliEf: Number(it.ibs_mun_ali_ef), ibsMunValor: Number(it.ibs_mun_valor),
          isSbas: Number(it.is_base), isSali: Number(it.is_aliq),
        } : {...ibsCbsVazio}
        return {
          ...it,
          icms_base_orig: Number(it.icms_base), icms_valor_orig: Number(it.icms_valor),
          pis_base_orig:  Number(it.pis_base),  pis_valor_orig:  Number(it.pis_valor),
          cofins_base_orig: Number(it.cofins_base), cofins_valor_orig: Number(it.cofins_valor),
          selecionado: true, qtd_devol: String(it.quantidade), expandido: true,
          edit_icms_cst: it.icms_cst || '41',
          edit_icms_base: Number(it.icms_base), edit_icms_aliq: Number(it.icms_aliq), edit_icms_valor: Number(it.icms_valor),
          edit_st_base: 0, edit_st_aliq: 0, edit_st_valor: 0,
          edit_ipi_cst: '53', edit_ipi_base: 0, edit_ipi_aliq: 0, edit_ipi_valor: 0,
          edit_pis_cst: '07',
          edit_pis_base: Number(it.pis_base), edit_pis_aliq: Number(it.pis_aliq), edit_pis_valor: Number(it.pis_valor),
          edit_cofins_cst: '07',
          edit_cofins_base: Number(it.cofins_base), edit_cofins_aliq: Number(it.cofins_aliq), edit_cofins_valor: Number(it.cofins_valor),
          ibsCbs, ibsCbsOrig: ibsCbs,
          frete_orig: Number(it.valor_frete) || 0, edit_frete: Number(it.valor_frete) || 0,
        }
      }))
      // Próximo número
      const empAtual = empresa ?? empresas.find(e => e.empcod === cabecalho.empcod)
      if (empAtual) {
        const nr = await api.get(`/erp/proximonumero/${empAtual.empcod}/NFE`)
        setProxSerie(String(nr.data.serie))
        setProxNumero(String(nr.data.numero))
        const cNF = randCNF()
        setChaveNFe(gerarChave(empAtual.empcgc, empAtual.empest, new Date().toISOString().slice(0,10), String(nr.data.serie), String(nr.data.numero), cNF))
      }
    } catch (e: any) {
      setErroBusca(e.response?.data?.error ?? 'Nota não encontrada')
    } finally { setBuscando(false) }
  }

  function atualizarQtd(idx: number, qtd: string) {
    setItens(prev => prev.map((it, i) => {
      if (i !== idx) return it
      const ratio = it.quantidade > 0 ? (Number(qtd)||0) / it.quantidade : 0
      return {
        ...it, qtd_devol: qtd,
        edit_icms_base: r2(it.icms_base_orig * ratio),
        edit_icms_valor: r2(it.icms_valor_orig * ratio),
        edit_pis_base: r2(it.pis_base_orig * ratio),
        edit_pis_valor: r2(it.pis_valor_orig * ratio),
        edit_cofins_base: r2(it.cofins_base_orig * ratio),
        edit_cofins_valor: r2(it.cofins_valor_orig * ratio),
        // Sem isso o vBC/vCBS/vIBS ficavam presos na quantidade cheia da nota de
        // entrada enquanto o vProd do item já vinha proporcional — cStat 1069/1070.
        ibsCbs: escalarIbsCbs(it.ibsCbsOrig, ratio),
        edit_frete: r2((it.frete_orig ?? 0) * ratio),
      }
    }))
  }

  // Frete editado: recalcula os impostos da natureza com o frete na base.
  function atualizarFrete(idx: number, valor: number) {
    setItens(prev => prev.map((it, i) => {
      if (i !== idx) return it
      const vProd = r2((Number(it.qtd_devol) || 0) * Number(it.preco_unitario))
      return { ...it, edit_frete: valor, ...(nat ? calcImpostosDevol(nat, vProd + valor) : {}) }
    }))
  }

  async function atualizarIbsCbs(cod: string, des: string) {
    setIbsCbsCod(cod); setIbsCbsDes(des)
    if (!cod) { setItens(prev => prev.map(it => ({...it, ibsCbs: {...ibsCbsVazio}, ibsCbsOrig: {...ibsCbsVazio}}))); return }
    try {
      const r = await api.get(`/erp/ibscbs/${cod}`)
      setItens(prev => prev.map(it => {
        // Calcula a regra pela quantidade CHEIA (it.quantidade) e guarda em
        // ibsCbsOrig — ibsCbs (na qtd_devol atual) é derivado disso por escalarIbsCbs,
        // mesma referência que atualizarQtd usa se a quantidade for editada depois.
        const vProdCheio = r2(it.quantidade * Number(it.preco_unitario))
        const ratioAtual = it.quantidade > 0 ? (Number(it.qtd_devol)||0) / it.quantidade : 0
        const baseCheia = Math.max(0, vProdCheio + (it.frete_orig ?? 0) - it.icms_valor_orig - it.pis_valor_orig - it.cofins_valor_orig)
        const orig = calcIbsCbs(baseCheia, r.data)
        return {...it, ibsCbs: escalarIbsCbs(orig, ratioAtual), ibsCbsOrig: orig}
      }))
    } catch {}
  }

  function editItem(idx: number, field: string, value: any) {
    setItens(prev => prev.map((it, i) => i === idx ? {...it, [field]: value} : it))
  }

  const itensSel = itens.filter(it => it.selecionado && (Number(it.qtd_devol)||0) > 0)

  // Informações complementares (infCpl do XML/DANFE): observação digitada + referência à nota de origem
  const refOrigem = cab
    ? `NF-e de devolução da nota ${cab.numero}, série ${String(cab.serie).trim()}, emitida em ${(cab.data_emissao || '').slice(0, 10).split('-').reverse().join('/')}, chave nº ${cab.chave}`
    : ''
  const infCplDevolucao = [obs.trim(), refOrigem].filter(Boolean).join('. ')
  const temQtdInvalida = itens.some(it => it.selecionado && (Number(it.qtd_devol)||0) > it.quantidade)
  const totalFrete = itensSel.reduce((s,it) => s + r2(it.edit_frete ?? 0), 0)
  const totalDevol = itensSel.reduce((s,it) => s + r2((Number(it.qtd_devol)||0)*Number(it.preco_unitario)), 0) + totalFrete

  const nat = naturezas.find(n => String(n.tno_cod) === naturezaCod)

  // tno_tin é só Isento/Tributado do cadastro, não o CST de IPI de verdade.
  function cstIpiDeIndicador(indicador: string): string {
    const i = indicador.trim().toUpperCase()
    if (i === 'I') return '53' // Saída não-tributada
    if (i === 'T') return '50' // Saída tributada
    return '53'
  }

  function calcImpostosDevol(n: Natureza, vProd: number) {
    const bc  = (base: number, p: number) => r2(base * (Number(p) / 100))
    const det = (base: number, a: number) => r2(base * (Number(a) / 100))
    const icmsBase   = bc(vProd, n.tno_bic);  const icmsValor   = det(icmsBase,   n.tno_aic)
    const ipiBase    = bc(vProd, n.tno_bin);  const ipiValor    = det(ipiBase,    n.tno_ain)
    const pisBase    = bc(vProd, n.tno_bpi);  const pisValor    = det(pisBase,    n.tno_api)
    const cofinsBase = bc(vProd, n.tno_bcf);  const cofinsValor = det(cofinsBase, n.tno_acf)
    return {
      edit_icms_cst: n.tno_tic?.trim() || '41',
      edit_icms_base: icmsBase, edit_icms_aliq: Number(n.tno_aic), edit_icms_valor: icmsValor,
      edit_ipi_cst: cstIpiDeIndicador(n.tno_tin ?? ''),
      edit_ipi_base: ipiBase,   edit_ipi_aliq:  Number(n.tno_ain), edit_ipi_valor:  ipiValor,
      edit_pis_cst: n.tno_tpi?.trim() || '07',
      edit_pis_base: pisBase,   edit_pis_aliq:  Number(n.tno_api), edit_pis_valor:  pisValor,
      edit_cofins_cst: n.tno_tcf?.trim() || '07',
      edit_cofins_base: cofinsBase, edit_cofins_aliq: Number(n.tno_acf), edit_cofins_valor: cofinsValor,
    }
  }

  // Recalcula impostos de todos os itens quando a natureza muda
  useEffect(() => {
    if (!nat || !itens.length) return
    setItens(prev => prev.map(it => {
      const vProd = r2((Number(it.qtd_devol) || it.quantidade) * Number(it.preco_unitario))
      return { ...it, ...calcImpostosDevol(nat, vProd + (it.edit_frete ?? 0)) }
    }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [naturezaCod])

  async function emitir() {
    const empAtual = empresa ?? empresas.find(e => e.empcod === cab?.empcod)
    if (!empAtual || !cab || !nat) return
    if (cab.bloqueado) { setErro('Cliente bloqueado — não é possível emitir documentos para ele.'); return }
    if (!tipdco) { setErro('Informe o Doc. Financeiro da devolução (não está definido na TPARVEN).'); return }
    const semNcm = itensSel.find(it => it.ncm.length !== 8)
    if (semNcm) { setErro(`Informe o NCM (8 dígitos) do item "${semNcm.descricao}" antes de transmitir.`); return }
    if (CFOPS_COMBUSTIVEL.has(String(nat.tno_cdf))) {
      const semAnp = itensSel.find(it => (it.comb_anp?.length ?? 0) !== 9)
      if (semAnp) { setErro(`CFOP ${nat.tno_cdf} exige o código ANP (Grupo Combustível) do item "${semAnp.descricao}" antes de transmitir.`); return }
    }
    setEnviando(true); setErro(null)

    // Recalcula chave com data de emissão atual
    const cNF  = randCNF()
    const chave = gerarChave(empAtual.empcgc, empAtual.empest, dataEmissao, proxSerie, proxNumero, cNF)

    const contri = cab.contribuinte?.trim().toUpperCase()
    const indIEDest = cab.ie?.trim() ? '1' : contri==='S' ? '2' : '9'
    const idDest = cab.uf?.trim().toUpperCase() === empAtual.empest?.trim().toUpperCase() ? '1' : '2'

    const itensNFe = itensSel.map(it => {
      const qtd   = Number(it.qtd_devol)||0
      const vProd = r2(qtd * Number(it.preco_unitario))
      return {
        cProd: String(it.its_cod), cEAN: 'SEM GTIN',
        xProd: it.descricao, NCM: it.ncm, CEST: it.cest||undefined,
        CFOP: String(nat.tno_cdf), uCom: it.unidade,
        qCom: qtd, vUnCom: Number(it.preco_unitario), vProd, indTot: '1' as const,
        vFrete: r2(it.edit_frete ?? 0) || undefined,
        combProdANP: it.comb_anp || undefined, combDescANP: it.comb_desc || undefined,
        impostos: {
          icmsCst: it.edit_icms_cst||'41', icmsOrig: '0',
          icmsBase: it.edit_icms_base, icmsAliq: it.edit_icms_aliq, icmsDest: it.edit_icms_valor,
          stBase: it.edit_st_base||undefined, stAliq: it.edit_st_aliq||undefined, stDest: it.edit_st_valor||undefined,
          ipiCst: it.edit_ipi_cst||'53', ipiBase: it.edit_ipi_base||undefined, ipiAliq: it.edit_ipi_aliq||undefined, ipiDest: it.edit_ipi_valor||undefined,
          pisCst: it.edit_pis_cst||'07', pisBase: it.edit_pis_base, pisAliq: it.edit_pis_aliq, pisDest: it.edit_pis_valor,
          cofinsCst: it.edit_cofins_cst||'07', cofinsBase: it.edit_cofins_base, cofinsAliq: it.edit_cofins_aliq, cofinsDest: it.edit_cofins_valor,
          ibsCbsCst:       it.ibsCbs.cst||undefined,
          ibsCbsClassTrib: it.ibsCbs.classTrib||undefined,
          cbsBcEf:   it.ibsCbs.cbsBcEf||undefined,  cbsAliEf:   it.ibsCbs.cbsAliEf||undefined,  cbsValor:   it.ibsCbs.cbsValor||undefined,
          ibsUfBcEf: it.ibsCbs.ibsUfBcEf||undefined, ibsUfAliEf: it.ibsCbs.ibsUfAliEf||undefined, ibsUfValor: it.ibsCbs.ibsUfValor||undefined,
          ibsMunBcEf:it.ibsCbs.ibsMunBcEf||undefined,ibsMunAliEf:it.ibsCbs.ibsMunAliEf||undefined,ibsMunValor:it.ibsCbs.ibsMunValor||undefined,
        },
      }
    })

    // Em modo edição reutiliza a chave já gerada; em nova emissão gera nova chave
    const chaveUsada = editId ? chaveNFe : chave

    const formPayload = {
      _tipo: 'DEVOLUCAO',
      empcod: empAtual.empcod,
      cab, itens, naturezaCod, dataEmissao, obs: obs.trim()||'', tipdco, tipdcoDes,
      proxSerie, proxNumero, chaveNFe: chaveUsada,
      finParcelas, finContaCod, ibsCbsCod, ibsCbsDes, modFrete,
    }

    const payload = {
      _formPayload: formPayload,
      empcod: empAtual.empcod, chave: chaveUsada, serie: proxSerie, numero: Number(proxNumero), dataEmissao,
      natureza: { cfop: nat.tno_cdf, descricao: nat.tno_des || nat.tno_dsc || 'DEVOLUCAO DE COMPRAS' },
      destinatario: {
        doc: cab.cnpj_fornecedor, fj: (cab.fj?.trim().toUpperCase()==='F'?'F':'J') as 'J'|'F',
        nome: cab.nome_fornecedor, ie: cab.ie||undefined, indIEDest,
        endereco: { logradouro: cab.logradouro, numero: cab.numero_end, bairro: cab.bairro, cidade: cab.cidade, uf: cab.uf, cep: cab.cep, telefone: cab.telefone||undefined },
      },
      tpNF: '1' as const, idDest, finNFe: '4' as const, indFinal: contri==='S' ? '0' : '1', indPres: '0', modFrete,
      itens: itensNFe,
      duplicatas: finParcelas.length>0 ? finParcelas.map(p=>({nDup:p.numero,dVenc:p.vencimento,vDup:parseFloat(p.valor)||0})).filter(d=>d.vDup>0) : undefined,
      tPag: '90', vPag: 0, obs: infCplDevolucao || undefined,
      nfRef: [cab.chave],
    }

    try {
      let id: number
      if (editId) {
        await api.put(`/nfe/${editId}`, payload)
        id = Number(editId)
      } else {
        const rs = await api.post('/nfe/salvar', { ...payload, _idSalvo: idSalvoRef.current ?? undefined })
        id = rs.data.id
        idSalvoRef.current = id
      }

      let autorizado = false
      try {
        const rt = await api.post(`/nfe/${id}/transmitir`)
        if (rt.data.autorizado) {
          autorizado = true
          setResultado(`✓ Autorizado — Protocolo ${rt.data.protocolo}`)
        } else if (rt.data.cStat === '204') {
          setResultado(`cStat 204: Nota já transmitida anteriormente.\n\nUse "Consultar SEFAZ" na lista de transmissão para recuperar o protocolo.`)
        } else {
          setResultado(`REJEITADO\n\ncStat ${rt.data.cStat}: ${rt.data.xMotivo}`)
        }
      } catch (et: any) {
        const detalhe = et?.response?.data?.error || et?.message || 'Erro'
        try {
          const rs2 = await api.get(`/nfe/${id}/status`)
          if (rs2.data.situacao==='AUTORIZADA') { autorizado=true; setResultado(`✓ Autorizado — Protocolo ${rs2.data.protocolo}`) }
          else setResultado(`ERRO AO TRANSMITIR\n\n${detalhe}`)
        } catch { setResultado(`ERRO AO TRANSMITIR\n\n${detalhe}`) }
      }

      setEtapa(2)
      if (autorizado) setTimeout(() => navigate('/nfe/lista'), 2500)
    } catch (e: any) {
      if (e?.response?.data?.codigo === 'NUMERO_EM_USO') setProxNumero(String(e.response.data.proximo))
      setErro(e?.response?.data?.error || 'Erro ao emitir')
    } finally { setEnviando(false) }
  }

  // ─── Etapa 2: resultado ───────────────────────────────────────────────────

  if (etapa === 2) {
    const autorizado = resultado?.startsWith('✓')
    return (
      <div style={{ maxWidth:600, margin:'40px auto', textAlign:'center' }}>
        <div style={{ background: autorizado?'#f0fdf4':'#fef2f2', border:`1px solid ${autorizado?'#86efac':'#fca5a5'}`, borderRadius:8, padding:'32px 24px' }}>
          <div style={{ fontSize:'2rem', marginBottom:8 }}>{autorizado ? '✓' : '✗'}</div>
          <div style={{ fontWeight:700, fontSize:'1rem', color: autorizado?'#15803d':'#dc2626', marginBottom:12, whiteSpace:'pre-wrap' }}>
            {resultado}
          </div>
          <div style={{ display:'flex', gap:8, justifyContent:'center' }}>
            <button className="btn btn-secondary" onClick={() => navigate('/nfe/lista')}>Ver Lista NF-e</button>
            {!autorizado && (
              <button className="btn btn-primary" onClick={() => { setEtapa(0); setCab(null); setItens([]); setResultado(null) }}>
                Nova Devolução
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }

  // ─── Etapa 1: Revisão ─────────────────────────────────────────────────────

  if (etapa === 1 && cab) {
    const empAtual = empresa ?? empresas.find(e => e.empcod === cab.empcod)
    return (
      <div style={{ maxWidth:900, margin:'0 auto' }}>

        {/* Resumo */}
        <div className="card" style={{ marginBottom:16 }}>
          <div style={{ fontWeight:700, fontSize:'0.95rem', marginBottom:10 }}>Revisão da Nota de Devolução</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'6px 24px', fontSize:'0.85rem' }}>
            <div><span style={{ color:'#9ca3af' }}>Empresa:</span> {empAtual?.empnom}</div>
            <div><span style={{ color:'#9ca3af' }}>Série/Nº:</span> {proxSerie}/{proxNumero}</div>
            <div style={{ gridColumn:'1/-1', display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ color:'#9ca3af', whiteSpace:'nowrap' }}>Natureza/CFOP:</span>
              <div style={{ flex:1, maxWidth:480 }}>
                <NaturezaSelect naturezas={naturezas} value={naturezaCod} onChange={setNaturezaCod} />
              </div>
            </div>
            <div style={{ gridColumn:'1/-1', display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ color:'#9ca3af', whiteSpace:'nowrap' }}>IBS/CBS — Regra:</span>
              <div style={{ flex:1, maxWidth:480 }}>
                {itens.some(it => it.ibsCbs.cst) && !ibsCbsForcarManual ? (
                  <div style={{
                    padding:'7px 10px', borderRadius:6, border:'1px solid #d1d5db',
                    background:'#f3f4f6', fontSize:'0.85rem', color:'#374151',
                  }}>
                    Valores de IBS/CBS obtidos da nota de entrada original — seletor desabilitado para não sobrescrevê-los.{' '}
                    <button type="button" onClick={() => setIbsCbsForcarManual(true)}
                      style={{ background:'none', border:'none', padding:0, color:'#1a6ea8', fontWeight:700, cursor:'pointer', textDecoration:'underline', fontSize:'inherit' }}>
                      Informar manualmente
                    </button>
                  </div>
                ) : (
                  <IbsCbsSelect codValue={ibsCbsCod} desValue={ibsCbsDes} onChange={atualizarIbsCbs} />
                )}
              </div>
            </div>
            <div><span style={{ color:'#9ca3af' }}>Fornecedor:</span> <strong>{cab.nome_fornecedor}</strong></div>
            <div><span style={{ color:'#9ca3af' }}>CNPJ:</span> {formatDoc(cab.cnpj_fornecedor)}</div>
            <div><span style={{ color:'#9ca3af' }}>Data:</span> {dataEmissao}</div>
            <div style={{ gridColumn:'1/-1' }}>
              <span style={{ color:'#9ca3af' }}>Ref:</span>{' '}
              <span style={{ fontFamily:'monospace', fontSize:'0.75rem' }}>{cab.chave}</span>
            </div>
          </div>
          <div style={{ marginTop:12, padding:'8px 10px', background:'#f9fafb', border:'1px solid #e5e7eb', borderRadius:6, fontSize:'0.82rem' }}>
            <div style={{ color:'#6b7280', fontWeight:600, marginBottom:3 }}>Informações complementares (vão no XML e no DANFE)</div>
            <div style={{ color:'#111827', wordBreak:'break-word' }}>{infCplDevolucao}</div>
          </div>
        </div>

        {/* Itens */}
        <div className="card" style={{ marginBottom:16 }}>
          <div style={{ fontWeight:600, fontSize:'0.85rem', marginBottom:8 }}>Itens ({itensSel.length})</div>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.8rem' }}>
            <thead>
              <tr className="thead-claro" style={{ background:'#f9fafb', borderBottom:'2px solid #e5e7eb' }}>
                <th style={{ padding:'5px 8px', width:32 }}></th>
                <th style={{ padding:'5px 8px', textAlign:'left' }}>Descrição</th>
                <th style={{ padding:'5px 8px', textAlign:'center' }}>Un</th>
                <th style={{ padding:'5px 8px', textAlign:'right' }}>Qtd</th>
                <th style={{ padding:'5px 8px', textAlign:'right' }}>Vl Unit</th>
                <th style={{ padding:'5px 8px', textAlign:'right' }}>Total</th>
                <th style={{ padding:'5px 8px', textAlign:'right' }}>Frete</th>
              </tr>
            </thead>
            <tbody>
              {itensSel.map((it, idx) => {
                const qtd = Number(it.qtd_devol)||0
                const vProd = r2(qtd*Number(it.preco_unitario))
                const origIdx = itens.findIndex(x => x.lin === it.lin)
                const aberto = !!expandRevisao[it.lin]
                return (
                  <Fragment key={it.lin}>
                    <tr style={{ borderBottom: aberto ? 'none' : '1px solid #f3f4f6' }}>
                      <td style={{ padding:'5px 8px', textAlign:'center' }}>
                        <button type="button" onClick={() => setExpandRevisao(p => ({...p, [it.lin]: !aberto}))}
                          style={{ border:'none', background:'transparent', cursor:'pointer', color:'#1a6ea8', fontSize:'0.75rem' }}
                          title="Ver impostos">
                          {aberto ? '▲' : '▼'}
                        </button>
                      </td>
                      <td style={{ padding:'5px 8px' }}>{it.descricao}</td>
                      <td style={{ padding:'5px 8px', textAlign:'center', color:'#6b7280' }}>{it.unidade}</td>
                      <td style={{ padding:'5px 8px', textAlign:'right' }}>{qtd.toLocaleString('pt-BR',{minimumFractionDigits:2})}</td>
                      <td style={{ padding:'5px 8px', textAlign:'right', color:'#6b7280' }}>{Number(it.preco_unitario).toLocaleString('pt-BR',{minimumFractionDigits:2})}</td>
                      <td style={{ padding:'5px 8px', textAlign:'right', fontWeight:600 }}>{brl(vProd)}</td>
                      <td style={{ padding:'5px 8px', textAlign:'right', color:'#6b7280' }}>{brl(it.edit_frete ?? 0)}</td>
                    </tr>
                    {aberto && (
                      <tr style={{ borderBottom:'1px solid #e5e7eb', background:'#f8fafc' }}>
                        <td colSpan={7} style={{ padding:'10px 16px 14px 36px' }}>
                          <PainelImpostosItem
                            it={it}
                            aba={abaItem(origIdx)}
                            onAba={a => setAbaFiscal(p=>({...p,[origIdx]:a}))}
                            onEdit={(field, value) => editItem(origIdx, field, value)}
                            cfop={nat ? String(nat.tno_cdf) : undefined}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Ações */}
        <div className="card">
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div>
              <div style={{ fontSize:'0.8rem', color:'#9ca3af' }}>{itensSel.length} {itensSel.length===1?'item':'itens'}</div>
              <div style={{ fontWeight:700, fontSize:'1.1rem' }}>Total: {brl(totalDevol)}{totalFrete > 0 && <span style={{ fontWeight:400, fontSize:'0.8rem', color:'#6b7280' }}> (inclui frete {brl(totalFrete)})</span>}</div>
            </div>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              {erro && <span style={{ color:'#dc2626', fontSize:'0.82rem', maxWidth:300 }}>{erro}</span>}
              <button className="btn btn-secondary" onClick={() => setEtapa(0)}>← Voltar</button>
              <button className="btn btn-primary" onClick={emitir} disabled={enviando || cab?.bloqueado} style={{ minWidth:160 }}>
                {enviando ? 'Transmitindo...' : editId ? 'Salvar e Transmitir →' : 'Emitir NF-e →'}
              </button>
            </div>
          </div>
        </div>

      </div>
    )
  }

  // ─── Etapa 0: Busca + Configuração ───────────────────────────────────────

  return (
    <div style={{ maxWidth:900, margin:'0 auto' }}>
      <AvisoUltimaNota empcod={empresa?.empcod ?? cab?.empcod} modelo="55" serie={proxSerie} proximo={proxNumero} onNumeroAjustado={n => setProxNumero(String(n))} />

      {/* Empresa */}
      <div className="card" style={{ marginBottom:16 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <label style={{ fontWeight:600, fontSize:'0.85rem', whiteSpace:'nowrap' }}>Estabelecimento</label>
          <select value={empresa?.empcod??''} onChange={e => setEmpresa(empresas.find(x=>x.empcod===Number(e.target.value))??null)}
            style={{ flex:1, padding:'6px 8px', borderRadius:6, border:'1px solid #d1d5db', fontSize:'0.9rem' }}>
            {empresas.map(em => <option key={em.empcod} value={em.empcod}>{em.empnom}</option>)}
          </select>
        </div>
      </div>

      {/* Busca */}
      <div className="card" style={{ marginBottom:16 }}>
        <div style={{ fontWeight:700, marginBottom:12, fontSize:'0.95rem' }}>Buscar nota de origem</div>
        <div style={{ marginBottom:10 }}>
          <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>Chave de acesso (44 dígitos)</label>
          <input value={chaveInput} onChange={e => setChaveInput(e.target.value.replace(/\D/g,'').slice(0,44))}
            placeholder="35250310..." maxLength={44}
            style={{ width:'100%', padding:'7px 10px', borderRadius:6, border:'1px solid #d1d5db', fontFamily:'monospace', fontSize:'0.85rem', boxSizing:'border-box' }} />
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10, margin:'10px 0', color:'#9ca3af', fontSize:'0.8rem' }}>
          <div style={{ flex:1, height:1, background:'#e5e7eb' }} />ou buscar por campos<div style={{ flex:1, height:1, background:'#e5e7eb' }} />
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr 80px 120px', gap:8, marginBottom:12 }}>
          <div>
            <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>Empcod</label>
            <input value={empcod} onChange={e=>setEmpcod(e.target.value)} style={{ width:'100%', padding:'7px 8px', borderRadius:6, border:'1px solid #d1d5db', boxSizing:'border-box' }} />
          </div>
          <div>
            <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>CNPJ/CPF Fornecedor</label>
            <input value={cnpj} onChange={e=>setCnpj(e.target.value)} placeholder="00.000.000/0000-00" style={{ width:'100%', padding:'7px 8px', borderRadius:6, border:'1px solid #d1d5db', boxSizing:'border-box' }} />
          </div>
          <div>
            <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>Série</label>
            <input value={serie} onChange={e=>setSerie(e.target.value)} style={{ width:'100%', padding:'7px 8px', borderRadius:6, border:'1px solid #d1d5db', boxSizing:'border-box' }} />
          </div>
          <div>
            <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>Número</label>
            <input type="number" value={numBusca} onChange={e=>setNumBusca(e.target.value)} style={{ width:'100%', padding:'7px 8px', borderRadius:6, border:'1px solid #d1d5db', boxSizing:'border-box' }} />
          </div>
        </div>
        <button className="btn btn-primary" onClick={buscar} disabled={buscando||!podeBuscar} style={{ width:'100%' }}>
          {buscando?'Buscando...':'Buscar'}
        </button>
        {erroBusca && <div style={{ marginTop:10, padding:'8px 12px', background:'#fef2f2', border:'1px solid #fca5a5', borderRadius:6, color:'#dc2626', fontSize:'0.85rem' }}>{erroBusca}</div>}
      </div>

      {cab && (
        <>
          {/* Cabeçalho nota original + próximo número */}
          <div className="card" style={{ marginBottom:16 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
              <div style={{ fontWeight:700, fontSize:'0.95rem', color:'#374151' }}>Nota de Origem</div>
              {proxNumero && (
                <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:6, padding:'4px 12px', fontSize:'0.82rem', color:'#1d4ed8' }}>
                  Próximo nº: <strong>Série {proxSerie} · Nº {proxNumero}</strong>
                </div>
              )}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'6px 24px', fontSize:'0.85rem' }}>
              <div><span style={{ color:'#9ca3af' }}>Fornecedor:</span> <strong>{cab.nome_fornecedor}</strong></div>
              <div><span style={{ color:'#9ca3af' }}>CNPJ:</span> {formatDoc(cab.cnpj_fornecedor)}</div>
              <div><span style={{ color:'#9ca3af' }}>UF:</span> {cab.uf}</div>
              <div><span style={{ color:'#9ca3af' }}>Série/Nº:</span> {cab.serie}/{cab.numero}</div>
              <div><span style={{ color:'#9ca3af' }}>Emissão:</span> {cab.data_emissao?.slice(0,10)}</div>
              <div><span style={{ color:'#9ca3af' }}>Total:</span> {brl(Number(cab.valor_total))}</div>
              <div style={{ gridColumn:'1/-1' }}>
                <span style={{ color:'#9ca3af' }}>Chave:</span>{' '}
                <span style={{ fontFamily:'monospace', fontSize:'0.75rem' }}>{cab.chave}</span>
              </div>
            </div>
            {cab.bloqueado && (
              <div style={{ background: '#fee2e2', color: '#991b1b', fontWeight: 700, fontSize: '0.85rem', padding: '0.5rem 0.75rem', borderRadius: 4, marginTop: '0.75rem' }}>
                ⚠ CLIENTE BLOQUEADO — não é possível emitir documentos para ele.
              </div>
            )}
          </div>

          {/* Configuração */}
          <div className="card" style={{ marginBottom:16 }}>
            <div style={{ fontWeight:700, marginBottom:12, fontSize:'0.95rem' }}>Dados da Devolução</div>
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>Natureza da Operação / CFOP</label>
              <NaturezaSelect naturezas={naturezas} value={naturezaCod} onChange={setNaturezaCod} />
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'160px 240px 1fr', gap:12, marginBottom:14 }}>
              <div>
                <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>Data Emissão</label>
                <input type="date" value={dataEmissao} onChange={e=>setDataEmissao(e.target.value)}
                  style={{ width:'100%', padding:'7px 8px', borderRadius:6, border:'1px solid #d1d5db', boxSizing:'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>
                  Doc. Financeiro {tipdcoParam === '' && <span style={{ color:'#dc2626' }}>*</span>}
                </label>
                {tipdcoParam ? (
                  <input value={`${tipdco}${tipdcoDes ? ` - ${tipdcoDes}` : ''}`} readOnly title="Definido na TPARVEN (Doc. Fin. p/ Devolução)"
                    style={{ width:'100%', padding:'7px 8px', borderRadius:6, border:'1px solid #d1d5db', boxSizing:'border-box', background:'#f3f4f6', color:'#374151' }} />
                ) : (
                  <LookupSelect
                    endpoint="/erp/doccad/lista" extraParams={{ es: 'S' }} codKey="doc_cod" descKey="doc_des" tagKey="doc_cod"
                    codValue={tipdco} descValue={tipdcoDes}
                    valorExibido={tipdco ? `${tipdco}${tipdcoDes ? ` - ${tipdcoDes}` : ''}` : ''}
                    onSelect={(cod, des) => { setTipdco(cod); setTipdcoDes(des) }}
                    placeholder="Não definido na TPARVEN — escolha..." listWidth={280}
                  />
                )}
              </div>
              <div>
                <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>Observação</label>
                <input value={obs} onChange={e=>setObs(e.target.value)} maxLength={60} placeholder="Opcional"
                  style={{ width:'100%', padding:'7px 8px', borderRadius:6, border:'1px solid #d1d5db', boxSizing:'border-box' }} />
              </div>
            </div>
            <div style={{ marginBottom:14, maxWidth:420 }}>
              <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>Modalidade do Frete</label>
              <select value={modFrete} onChange={e=>setModFrete(e.target.value as any)}
                style={{ width:'100%', padding:'7px 8px', borderRadius:6, border:'1px solid #d1d5db', boxSizing:'border-box' }}>
                <option value="0">0 - Por conta do remetente (CIF)</option>
                <option value="1">1 - Por conta do destinatário (FOB)</option>
                <option value="2">2 - Por conta de terceiros</option>
                <option value="3">3 - Próprio, por conta do remetente</option>
                <option value="4">4 - Próprio, por conta do destinatário</option>
                <option value="9">9 - Sem ocorrência de transporte</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>IBS/CBS — Regra (aplicada a todos os itens)</label>
              {itens.some(it => it.ibsCbs.cst) && !ibsCbsForcarManual ? (
                <div style={{
                  padding:'7px 10px', borderRadius:6, border:'1px solid #d1d5db',
                  background:'#f3f4f6', fontSize:'0.85rem', color:'#374151',
                }}>
                  Valores de IBS/CBS obtidos da nota de entrada original — seletor de regra desabilitado para não sobrescrevê-los.{' '}
                  <button type="button" onClick={() => setIbsCbsForcarManual(true)}
                    style={{ background:'none', border:'none', padding:0, color:'#1a6ea8', fontWeight:700, cursor:'pointer', textDecoration:'underline', fontSize:'inherit' }}>
                    Informar manualmente
                  </button>
                </div>
              ) : (
                <IbsCbsSelect codValue={ibsCbsCod} desValue={ibsCbsDes} onChange={atualizarIbsCbs} />
              )}
            </div>
          </div>

          {/* Itens */}
          <div className="card" style={{ marginBottom:16 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
              <span style={{ fontWeight:700, fontSize:'0.95rem' }}>Itens</span>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={() => setItens(prev=>prev.map(it=>({...it,selecionado:true,qtd_devol:String(it.quantidade)})))}
                  style={{ fontSize:'0.78rem', padding:'3px 10px', borderRadius:4, border:'1px solid #d1d5db', background:'#f9fafb', cursor:'pointer' }}>Todos</button>
                <button onClick={() => setItens(prev=>prev.map(it=>({...it,selecionado:false})))}
                  style={{ fontSize:'0.78rem', padding:'3px 10px', borderRadius:4, border:'1px solid #d1d5db', background:'#f9fafb', cursor:'pointer' }}>Nenhum</button>
              </div>
            </div>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
                <thead>
                  <tr className="thead-claro" style={{ background:'#f9fafb', borderBottom:'2px solid #e5e7eb' }}>
                    <th style={{ padding:'6px 8px', width:32 }}></th>
                    <th style={{ padding:'6px 8px', textAlign:'left' }}>Descrição</th>
                    <th style={{ padding:'6px 8px', textAlign:'center' }}>Un</th>
                    <th style={{ padding:'6px 8px', textAlign:'right' }}>Qtd Original</th>
                    <th style={{ padding:'6px 8px', textAlign:'right' }}>Qtd Devolução</th>
                    <th style={{ padding:'6px 8px', textAlign:'right' }}>Vl Unit</th>
                    <th style={{ padding:'6px 8px', textAlign:'right' }}>Total</th>
                    <th style={{ padding:'6px 8px', textAlign:'right' }}>Frete</th>
                    <th style={{ padding:'6px 8px', textAlign:'center' }}>Impostos</th>
                  </tr>
                </thead>
                <tbody>
                  {itens.map((it, idx) => {
                    const qtdDevol = Number(it.qtd_devol)||0
                    const qtdOrig  = Number(it.quantidade)
                    const excede   = it.selecionado && qtdDevol > qtdOrig
                    const parcial  = it.selecionado && qtdDevol > 0 && qtdDevol < qtdOrig
                    const bordaCor = excede?'#f87171':parcial?'#fbbf24':'#d1d5db'
                    const bgInput  = excede?'#fff1f2':parcial?'#fffbeb':undefined
                    const totalIt  = r2(qtdDevol * Number(it.preco_unitario))
                    return (
                      <Fragment key={idx}>
                        <tr style={{ borderBottom: it.expandido?'none':(excede||parcial)?'none':'1px solid #f3f4f6', background:it.selecionado?'#fff':'#fafafa', opacity:it.selecionado?1:0.5 }}>
                          <td style={{ padding:'6px 8px', textAlign:'center' }}>
                            <input type="checkbox" checked={it.selecionado} onChange={() => editItem(idx,'selecionado',!it.selecionado)} />
                          </td>
                          <td style={{ padding:'6px 8px', fontWeight:500 }}>{it.descricao}</td>
                          <td style={{ padding:'6px 8px', textAlign:'center', color:'#6b7280' }}>{it.unidade}</td>
                          <td style={{ padding:'6px 8px', textAlign:'right', color:'#6b7280' }}>{qtdOrig.toLocaleString('pt-BR',{minimumFractionDigits:2})}</td>
                          <td style={{ padding:'6px 4px', textAlign:'right' }}>
                            <input type="number" value={it.qtd_devol} onChange={e=>atualizarQtd(idx,e.target.value)} disabled={!it.selecionado} min={0} step="0.001"
                              style={{ width:80, textAlign:'right', padding:'3px 6px', borderRadius:4, border:`1px solid ${bordaCor}`, background:bgInput, fontSize:'0.82rem' }} />
                          </td>
                          <td style={{ padding:'6px 8px', textAlign:'right' }}>{Number(it.preco_unitario).toLocaleString('pt-BR',{minimumFractionDigits:2})}</td>
                          <td style={{ padding:'6px 8px', textAlign:'right', fontWeight:600 }}>{brl(totalIt)}</td>
                          <td style={{ padding:'6px 4px', textAlign:'right' }}>
                            <input type="number" value={it.edit_frete ?? 0} onChange={e=>atualizarFrete(idx, Number(e.target.value)||0)} disabled={!it.selecionado} min={0} step="0.01"
                              title={it.frete_orig ? `Frete na nota de entrada: ${brl(it.frete_orig)}` : undefined}
                              style={{ width:80, textAlign:'right', padding:'3px 6px', borderRadius:4, border:'1px solid #d1d5db', fontSize:'0.82rem' }} />
                          </td>
                          <td style={{ padding:'6px 8px', textAlign:'center' }}>
                            <button onClick={() => editItem(idx,'expandido',!it.expandido)} disabled={!it.selecionado}
                              style={{ fontSize:'0.75rem', padding:'2px 8px', borderRadius:4, border:'1px solid #d1d5db', background:'#f9fafb', cursor:'pointer', color:'#374151' }}>
                              {it.expandido ? 'Fechar ▲' : 'Ver ▼'}
                            </button>
                          </td>
                        </tr>
                        {(excede || parcial) && (
                          <tr key={`${idx}-aviso`} style={{ borderBottom: it.expandido?'none':'1px solid #f3f4f6' }}>
                            <td colSpan={9} style={{ padding:'2px 8px 6px 44px', fontSize:'0.75rem', color:excede?'#dc2626':'#92400e' }}>
                              {excede ? `Quantidade maior que a original (${qtdOrig.toLocaleString('pt-BR',{minimumFractionDigits:2})})` : `Devolução parcial — original: ${qtdOrig.toLocaleString('pt-BR',{minimumFractionDigits:2})}`}
                            </td>
                          </tr>
                        )}
                        {it.expandido && (
                          <tr style={{ borderBottom:'1px solid #e5e7eb', background:'#f8fafc' }}>
                            <td colSpan={9} style={{ padding:'10px 16px 14px 36px' }}>
                              <PainelImpostosItem
                                it={it}
                                aba={abaItem(idx)}
                                onAba={a => setAbaFiscal(p=>({...p,[idx]:a}))}
                                onEdit={(field, value) => editItem(idx, field, value)}
                                cfop={nat ? String(nat.tno_cdf) : undefined}
                              />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Rodapé */}
          <div className="card">
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <div style={{ fontSize:'0.8rem', color:'#9ca3af' }}>{itensSel.length} {itensSel.length===1?'item':'itens'} selecionado{itensSel.length!==1?'s':''}</div>
                <div style={{ fontWeight:700, fontSize:'1.1rem' }}>Total: {brl(totalDevol)}{totalFrete > 0 && <span style={{ fontWeight:400, fontSize:'0.8rem', color:'#6b7280' }}> (inclui frete {brl(totalFrete)})</span>}</div>
              </div>
              <button
                className="btn btn-primary"
                onClick={() => setEtapa(1)}
                disabled={itensSel.length===0 || temQtdInvalida || !naturezaCod || cab?.bloqueado}
                style={{ minWidth:180 }}
              >
                Revisar →
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
